import {
  AuthenticationResult,
  EventType,
  IPublicClientApplication,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import { useMsal, useMsalAuthentication } from '@azure/msal-react';
import { getUser } from 'API';
import { b2cPolicies, loginRequest } from 'B2C';
import { B2C_ERROR_STATUS } from 'Enum/B2C';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { login, useAppSelector, useDispatchApp, useImposter } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { setIsAdmin } from 'Store';
import { compareIssuingPolicy, getTokenFromUrl } from 'Utils';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const B2CGuard: React.FC<any> = (props) => {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const [isError, setIsError] = useState<
    HTTP_STATUS_CODES.NotFound | HTTP_STATUS_CODES.Unauthorized | ''
  >('');
  const { isAdmin } = useImposter();
  const { loader, isLoading, setIsLoading } = useLoader();

  const otpData = useAppSelector(
    ({ otpLoginDetails: { otpLoginDetails } }) => otpLoginDetails
  );

  const oldToken = sessionStorage.getItem('token') || getTokenFromUrl();

  const isNoTokenAndFromOldSite = useCallback(
    () =>
      !(
        getTokenFromUrl() ||
        otpData?.token ||
        sessionStorage.getItem('B2CToken') ||
        sessionStorage.getItem('token')
      ) && document.referrer === import.meta.env.VITE_APP_B2C_REFERRER,
    [otpData?.token]
  );

  useEffect(() => {
    if (isNoTokenAndFromOldSite()) login();
  }, [isNoTokenAndFromOldSite, otpData?.token]);

  const { result, error } = useMsalAuthentication(InteractionType.None, {
    scopes: loginRequest.scopes,
  });

  const dispatch = useDispatchApp();

  const loginRedirectHandler = useCallback(
    async (instance: IPublicClientApplication) => {
      if (inProgress === InteractionStatus.None) {
        setFirstTimeValue(true);
        await instance.loginRedirect(loginRequest);
      }
    },
    [inProgress]
  );

  const setFirstTimeValue = function (value: boolean) {
    sessionStorage.setItem('isFirstTime', value.toString());
  };

  const newUserHandler = useCallback(
    async (instance: IPublicClientApplication, oldToken: string) => {
      if (inProgress === InteractionStatus.None) {
        sessionStorage.removeItem('token');
        await instance.acquireTokenRedirect({
          ...b2cPolicies.authorities.newUser,
          extraQueryParameters: { oldToken },
        });
      }
    },
    [inProgress]
  );

  useEffect(() => {
    if (
      result &&
      compareIssuingPolicy(result.idTokenClaims, b2cPolicies.names.signUpSignIn)
    ) {
      sessionStorage.setItem('B2CToken', result.accessToken);

      var firstTimeValue = sessionStorage.getItem('isFirstTime');
      if (
        (result?.idTokenClaims as any)?.newUser &&
        (!firstTimeValue || firstTimeValue !== 'true')
      ) {
        setFirstTimeValue(false);
        navigate('/contact-details');
      }
    } else if (error?.errorMessage?.includes('invalid')) {
      setFirstTimeValue(true);
      instance.loginRedirect();
    } else if (!error && result?.idTokenClaims)
      sessionStorage.setItem('B2CToken', result.accessToken);
  }, [result, error, instance]);

  useEffect(() => {
    let malInstanceCallbackId: string | null = null;
    if (instance) {
      malInstanceCallbackId = instance.addEventCallback((event: any) => {
        const { eventType, payload } = event;
        if (
          (eventType === EventType.LOGIN_SUCCESS ||
            eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            eventType === EventType.SSO_SILENT_SUCCESS) &&
          payload
        ) {
          if (
            compareIssuingPolicy(
              event.payload.idTokenClaims,
              b2cPolicies.names.editProfile
            ) ||
            compareIssuingPolicy(
              event.payload.idTokenClaims,
              b2cPolicies.names.newUser
            )
          ) {
            let signUpSignInFlowRequest = {
              authority: b2cPolicies.authorities.signUpSignIn.authority,
              account: instance.getAllAccounts()[0],
              scopes: loginRequest.scopes,
            };

            // silently login again with the signUpSignIn policy
            instance.ssoSilent(signUpSignInFlowRequest);
          } else {
            const eventPayload = payload as AuthenticationResult;
            instance.setActiveAccount(eventPayload.account);
          }
        }
        if (
          event.error?.errorCode === 'server_error' &&
          event.error?.message.includes('AADB2C: An exception has occurred')
        ) {
          const xToken = sessionStorage.getItem('B2CToken');
          if (xToken) {
            setFirstTimeValue(true);
            instance.loginRedirect({
              ...b2cPolicies.authorities.editProfile,
              extraQueryParameters: {
                xToken,
              },
            });
          }
        }
        if (eventType === EventType.LOGIN_FAILURE) {
          localStorage.clear();
          if (event.error?.message.includes(B2C_ERROR_STATUS.STATUS_404)) {
            setIsError(HTTP_STATUS_CODES.NotFound);
            login();
          }
          if (event.error?.message === B2C_ERROR_STATUS.STATUS_400) {
            setFirstTimeValue(true);
            instance.loginRedirect(loginRequest);
          }
          if (
            [
              B2C_ERROR_STATUS.STATUS_401,
              B2C_ERROR_STATUS.INVALID_OLD_TOKEN_V1,
              B2C_ERROR_STATUS.INVALID_OLD_TOKEN_V2,
              B2C_ERROR_STATUS.INVALID_PASSWORD_SPAM,
            ].some((option: string) => event.error?.message.includes(option))
          ) {
            setIsError(HTTP_STATUS_CODES.Unauthorized);
            window.location.assign('/B2CError');
          }
        }
      });
    }

    return () => {
      if (malInstanceCallbackId)
        instance.removeEventCallback(malInstanceCallbackId);
    };
  }, [instance, navigate]);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      const activeAccount = instance.getActiveAccount();

      if (!activeAccount && !isError && !oldToken) {
        if (!isNoTokenAndFromOldSite()) loginRedirectHandler(instance);
      } else if (!activeAccount && oldToken) {
        (async () => {
          const user = await getUser();
          if (user?.isAdminAuthenticatedUser) {
            dispatch(setIsAdmin(true));
          } else {
            localStorage.removeItem('token');

            if (
              error?.message.includes(B2C_ERROR_STATUS.STATUS_400) &&
              (getTokenFromUrl() || sessionStorage.getItem('token'))
            ) {
              instance.loginRedirect(loginRequest);
              return;
            }

            newUserHandler(instance, oldToken);
          }
        })();
      }
    }
  }, [
    instance,
    inProgress,
    isError,
    oldToken,
    isAdmin,
    result,
    loginRedirectHandler,
    newUserHandler,
    dispatch,
  ]);

  useEffect(() => {
    setIsLoading(true);

    const timeOut = setTimeout(() => {
      if (inProgress === InteractionStatus.None) setIsLoading(false);
    }, 0);

    return () => clearTimeout(timeOut);
  }, [inProgress, setIsLoading]);

  return (
    <>
      {!isLoading &&
      inProgress === InteractionStatus.None &&
      ((result &&
        compareIssuingPolicy(
          result.idTokenClaims,
          b2cPolicies.names.signUpSignIn
        )) ||
        isError === HTTP_STATUS_CODES.Unauthorized ||
        isAdmin)
        ? props.children
        : loader}
    </>
  );
};
